import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Edit, Globe, Package, Users, MapPin } from 'lucide-react';
import { companiesAPI, domainsAPI, companyPaymentsAPI, customersAPI } from '../lib/api';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [company, setCompany] = useState(null);
  const [domains, setDomains] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      
      // Şirket bilgilerini getir
      const companyData = await companiesAPI.getById(id);
      setCompany(companyData.data || companyData);
      
      // Tüm domainleri getir ve şirkete göre filtrele
      const allDomains = await domainsAPI.getAll();
      const companyDomains = allDomains.filter(d => String(d.company_id) === String(id));
      setDomains(companyDomains);
      
      // Tüm siparişleri getir ve şirkete göre filtrele
      const allPayments = await companyPaymentsAPI.getAll();
      const companyPayments = allPayments.filter(p => String(p.company_id) === String(id));
      setPayments(companyPayments);
      
      // Tüm müşterileri getir ve şirketle ilişkili olanları filtrele
      try {
        const allCustomers = await customersAPI.getAll();
        const relatedCustomers = allCustomers.filter(c => 
          c.companies && c.companies.some(comp => String(comp.id) === String(id))
        );
        setCustomers(relatedCustomers);
      } catch (customerErr) {
        console.error('Müşteriler yüklenirken hata:', customerErr);
        setCustomers([]);
      }
      
    } catch (err) {
      console.error('Şirket detayları yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  // İstatistikleri hesapla
  const calculateStats = () => {
    const totalDomains = domains.length;
    const activeDomains = domains.filter(d => d.status === 'active' || d.is_active !== false).length;
    
    const totalPayments = payments.length;
    const activePayments = payments.filter(p => p.is_active !== false).length;
    
    const totalRevenue = payments.reduce((sum, p) => sum + parseFloat(p.sale_price || 0), 0);
    const totalCost = payments.reduce((sum, p) => sum + parseFloat(p.purchase_price || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    
    return {
      totalDomains, activeDomains,
      totalPayments, activePayments,
      totalRevenue, totalCost, totalProfit,
      totalCustomers: customers.length
    };
  };

  const stats = calculateStats();

  const getPackageTypeText = (type) => {
    const types = {
      domain: 'Domain',
      hosting: 'Hosting',
      ssl: 'SSL',
      email: 'E-posta',
      reklam: 'Reklam',
      danismanlik: 'Danışmanlık',
      other: 'Diğer'
    };
    return types[type] || type || 'Diğer';
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'TRY': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };
    return symbols[currency] || currency;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Şirket bulunamadı
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-8 py-4">
          {/* Navigasyon */}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
            <button onClick={() => navigate('/companies')} className="hover:text-blue-600">
              Firmalar
            </button>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{company.name}</span>
          </div>

          {/* Başlık */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Building className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{company.name}</h1>
                <p className="text-gray-600 dark:text-gray-400">{company.email || company.phone || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/companies', { state: { editCompany: company } })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Edit size={18} />
                Düzenle
              </button>
              <button
                onClick={() => navigate(`/companies`)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                Geri
              </button>
            </div>
          </div>

          {/* Sekmeler */}
          <div className="flex gap-1 mt-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summary'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Özet
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'info'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Bilgiler
            </button>
            <button
              onClick={() => setActiveTab('domains')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'domains'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Domainler ({stats.totalDomains})
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'payments'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Siparişler ({stats.totalPayments})
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'customers'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Müşteriler ({stats.totalCustomers})
            </button>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="p-8">
        {activeTab === 'summary' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Şirket Bilgileri */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Şirket Bilgileri</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Şirket Adı</span>
                  <span className="font-medium text-gray-900 dark:text-white">{company.name}</span>
                </div>
                {company.tax_number && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Vergi No</span>
                    <span className="font-medium text-gray-900 dark:text-white">{company.tax_number}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Telefon</span>
                    <span className="font-medium text-gray-900 dark:text-white">{company.phone}</span>
                  </div>
                )}
                {company.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">E-Posta</span>
                    <span className="font-medium text-gray-900 dark:text-white">{company.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Durum</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    company.is_active !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {company.is_active !== false ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
            </div>

            {/* Finansal Özet */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Finansal Özet</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Toplam Gelir</span>
                  <span className="font-medium text-green-600">
                    ₺{stats.totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Toplam Maliyet</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    ₺{stats.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Net Kar</span>
                  <span className={`font-bold ${stats.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₺{stats.totalProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Varlıklar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Varlıklar</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-blue-600" />
                    <span className="text-gray-600 dark:text-gray-400">Domainler</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stats.totalDomains} ({stats.activeDomains} aktif)
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-purple-600" />
                    <span className="text-gray-600 dark:text-gray-400">Siparişler</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stats.totalPayments} ({stats.activePayments} aktif)
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-green-600" />
                    <span className="text-gray-600 dark:text-gray-400">Müşteriler</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stats.totalCustomers}
                  </span>
                </div>
              </div>
            </div>

            {/* Adres */}
            {(company.address || company.country || company.state) && (
              <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Adres Bilgileri</h3>
                <div className="flex items-start gap-2">
                  <MapPin className="text-gray-400 mt-1" size={18} />
                  <div>
                    {company.address && <p className="text-gray-700 dark:text-gray-300">{company.address}</p>}
                    {(company.state || company.country) && (
                      <p className="text-gray-500 dark:text-gray-400">
                        {[company.state, company.country].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Notlar */}
            {company.description && (
              <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Açıklama / Notlar</h3>
                <p className="text-gray-700 dark:text-gray-300">{company.description}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Şirket Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Şirket Adı</label>
                <p className="text-gray-900 dark:text-white">{company.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Vergi No</label>
                <p className="text-gray-900 dark:text-white">{company.tax_number || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Telefon</label>
                <p className="text-gray-900 dark:text-white">{company.phone || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">E-Posta</label>
                <p className="text-gray-900 dark:text-white">{company.email || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Website</label>
                {company.website ? (
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{company.website}</a>
                ) : (
                  <p className="text-gray-900 dark:text-white">-</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Durum</label>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  company.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {company.is_active !== false ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Adres</label>
                <p className="text-gray-900 dark:text-white">{company.address || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Ülke / Şehir</label>
                <p className="text-gray-900 dark:text-white">
                  {[company.state, company.country].filter(Boolean).join(', ') || '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Kayıt Tarihi</label>
                <p className="text-gray-900 dark:text-white">
                  {company.created_at ? new Date(company.created_at).toLocaleDateString('tr-TR') : '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Son Güncelleme</label>
                <p className="text-gray-900 dark:text-white">
                  {company.updated_at ? new Date(company.updated_at).toLocaleDateString('tr-TR') : '-'}
                </p>
              </div>
            </div>

            {company.description && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Açıklama</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  {company.description}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'domains' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Domainler ({stats.totalDomains})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Domain</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Kayıt Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Bitiş Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Registrar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {domains.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Kayıtlı domain yok
                      </td>
                    </tr>
                  ) : (
                    domains.map((domain, idx) => (
                      <tr key={domain.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Globe size={16} className="text-blue-600" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {domain.domain_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            domain.status === 'active' || domain.is_active !== false
                              ? 'bg-green-100 text-green-700'
                              : domain.status === 'expired'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {domain.status === 'active' ? 'Aktif' : domain.status === 'expired' ? 'Süresi Dolmuş' : domain.status || 'Aktif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {domain.registration_date ? new Date(domain.registration_date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {domain.expiry_date ? new Date(domain.expiry_date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {domain.registrar_name || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Siparişler ({stats.totalPayments})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Paket</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Tür</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Alış</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Satış</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Kar</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Sonraki Ödeme</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Kayıtlı sipariş yok
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment, idx) => {
                      const purchasePrice = parseFloat(payment.purchase_price || 0);
                      const salePrice = parseFloat(payment.sale_price || 0);
                      const profit = salePrice - purchasePrice;
                      
                      return (
                        <tr key={payment.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {payment.package_name || 'Sipariş'}
                              </span>
                              {payment.domain_name && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                  <Globe size={10} />
                                  {payment.domain_name}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              payment.package_type === 'domain' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              payment.package_type === 'hosting' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                              payment.package_type === 'ssl' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {getPackageTypeText(payment.package_type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-gray-700 dark:text-gray-300">
                              {getCurrencySymbol(payment.purchase_currency || 'TRY')} {purchasePrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {getCurrencySymbol(payment.sale_currency || 'TRY')} {salePrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className={`font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ₺{profit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                            {payment.next_payment_date ? new Date(payment.next_payment_date).toLocaleDateString('tr-TR') : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              payment.is_active !== false
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {payment.is_active !== false ? 'Aktif' : 'Pasif'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Müşteriler ({stats.totalCustomers})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Müşteri</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">E-Posta</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Telefon</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Bağlı müşteri yok
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer, idx) => (
                      <tr key={customer.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Users size={16} className="text-green-600" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {customer.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {customer.email || '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {customer.phone || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            customer.is_active !== false
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {customer.is_active !== false ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => navigate(`/customers/${customer.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Detay
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetail;
